/*──────────────────────────────────────────────────────────────
 * src/components/TagsTab.jsx — FULL REGEN 2026-01-21
 * • Admin-only Tags manager (per-company)
 * • GET tags via clientDetails.php?company=...&tags=1
 * • CREATE via POST clientDetails.php?company=...&createTags=1
 * • EDIT via PATCH clientDetails.php?company=...&action=upsertTag
 * • HARD GUARD: Create will NOT overwrite an existing TagID.
 *   If TagID exists, prompts “Modify existing?” and switches to Edit.
 * • TagID is locked in Edit mode (PK)
 * • Search / sort / CSV export
 * • Self-contained: can run with internal state, or accept tags/setTags props
 * • ADMIN: accepts role "C" OR "A"
 *──────────────────────────────────────────────────────────────*/

import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTimes,
  faSortUp,
  faSortDown,
  faSearch,
  faFileExport,
  faTag,
} from "@fortawesome/free-solid-svg-icons";

/* ───────────── helpers ───────────── */
const getInputClasses = (th) =>
  `w-full border px-2 py-1 text-sm rounded focus:outline-none focus:ring-2 focus:ring-blue-400 ${
    th === "dark"
      ? "bg-gray-700 text-gray-100 border-gray-600"
      : "border-gray-300"
  }`;

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const CLIENT_DETAILS = (baseUrl) =>
  `${String(baseUrl || "https://house.ogendeavors.com")
    .replace(/\/+$/, "")
    .trim()}/api/clientDetails.php`;

const TAGS_GET_ENDPOINT = (baseUrl, company) =>
  `${CLIENT_DETAILS(baseUrl)}?company=${encodeURIComponent(
    company || "",
  )}&tags=1`;

const TAGS_CREATE_ENDPOINT = (baseUrl, company) =>
  `${CLIENT_DETAILS(baseUrl)}?company=${encodeURIComponent(
    company || "",
  )}&createTags=1`;

const TAG_UPSERT_ENDPOINT = (baseUrl, company) =>
  `${CLIENT_DETAILS(baseUrl)}?company=${encodeURIComponent(
    company || "",
  )}&action=upsertTag`;

/* Normalize a tag record */
const normalizeTag = (t) => {
  const TagID = String(t?.TagID ?? "").trim();
  const TagDesc = String(t?.TagDesc ?? "").trim();
  return { TagID, TagDesc };
};

const dedupeTags = (arr) => {
  const map = new Map();
  (arr || []).forEach((t) => {
    const nt = normalizeTag(t);
    if (!nt.TagID) return;
    map.set(nt.TagID.toLowerCase(), nt);
  });
  return Array.from(map.values());
};

/* ───────────── component ───────────── */
function TagsTab({
  theme,
  companyName,
  role = "A",
  baseUrl = "https://house.ogendeavors.com",
  tags: tagsProp,
  setTags: setTagsProp,
}) {
  /* Admin-only (accepts "C" or "A") */
  const r = String(role || "").toUpperCase();
  const isAdmin = r === "C" || r === "A";

  /* internal state fallback if parent doesn't pass tags/setTags */
  const [internalTags, setInternalTags] = useState([]);
  const tags = Array.isArray(tagsProp) ? tagsProp : internalTags;
  const setTags =
    typeof setTagsProp === "function" ? setTagsProp : setInternalTags;

  /* ui state */
  const [loading, setLoading] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);

  const [tagModalMode, setTagModalMode] = useState("create"); // "create" | "edit"
  const [originalTagId, setOriginalTagId] = useState("");

  const [editingTag, setEditingTag] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("TagID");
  const [sortDirection, setSortDirection] = useState("asc");

  const blankTag = { TagID: "", TagDesc: "" };

  /* loaders */
  const fetchTags = async () => {
    if (!companyName || !isAdmin) {
      setTags([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(TAGS_GET_ENDPOINT(baseUrl, companyName), {
        method: "GET",
      });
      const data = await res.json();

      // Accepted shapes:
      // A) [{TagID,TagDesc}, ...]
      // B) { tags:[...] }
      // C) [{..., tags:[...]}]
      let arr = [];
      if (Array.isArray(data)) {
        if (data.length && Array.isArray(data?.[0]?.tags)) arr = data[0].tags;
        else arr = data;
      } else if (Array.isArray(data?.tags)) {
        arr = data.tags;
      }

      const cleaned = dedupeTags(arr)
        .filter((t) => t.TagID)
        .sort((a, b) => collator.compare(a.TagID, b.TagID));

      setTags(cleaned);
    } catch (e) {
      console.error("Failed to load tags:", e);
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  /* load tags on mount/company change (admin only) */
  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, isAdmin]);

  /* modal actions */
  const handleCreateTag = () => {
    setTagModalMode("create");
    setOriginalTagId("");
    setEditingTag({ ...blankTag });
    setShowTagModal(true);
  };

  const handleEditTag = (t) => {
    const nt = normalizeTag(t);
    setTagModalMode("edit");
    setOriginalTagId(nt.TagID);
    setEditingTag(nt);
    setShowTagModal(true);
  };

  const handleCancelTag = () => {
    setShowTagModal(false);
    setEditingTag(null);
    setTagModalMode("create");
    setOriginalTagId("");
  };

  const handleModalFieldChange = (e) => {
    const { name, value } = e.target;

    // Prevent PK change in edit mode
    if (name === "TagID" && tagModalMode === "edit") return;

    setEditingTag((p) => ({ ...(p || {}), [name]: value }));
  };

  const handleSaveTag = async () => {
    if (!editingTag || !isAdmin) return;

    const enteredId = String(editingTag.TagID || "").trim();
    const enteredDesc = String(editingTag.TagDesc || "").trim();

    if (!enteredId) {
      alert("TagID is required.");
      return;
    }
    if (!enteredDesc) {
      alert("TagDesc is required.");
      return;
    }

    const existingLocal = tags.find(
      (t) => String(t.TagID || "").toLowerCase() === enteredId.toLowerCase(),
    );

    // HARD GUARD: Create must not overwrite.
    if (tagModalMode === "create" && existingLocal) {
      const goEdit = window.confirm(
        `TagID "${existingLocal.TagID}" already exists.\n\nDo you want to modify the existing tag instead?`,
      );
      if (goEdit) {
        setTagModalMode("edit");
        setOriginalTagId(String(existingLocal.TagID || ""));
        setEditingTag({ ...existingLocal });
      } else {
        alert(
          `Create cancelled.\n\nChoose a different TagID to create a NEW tag.`,
        );
      }
      return;
    }

    const payload = {
      TagID: enteredId,
      TagDesc: enteredDesc,
    };

    const isCreate = tagModalMode === "create";
    const patchTargetTagId = String(originalTagId || enteredId || "").trim();

    const url = isCreate
      ? TAGS_CREATE_ENDPOINT(baseUrl, companyName)
      : TAG_UPSERT_ENDPOINT(baseUrl, companyName);

    const method = isCreate ? "POST" : "PATCH";

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          isCreate
            ? payload
            : { TagID: patchTargetTagId, TagDesc: enteredDesc },
        ),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out?.error || "Unknown server error");

      const saved = normalizeTag(
        isCreate ? payload : { TagID: patchTargetTagId, TagDesc: enteredDesc },
      );

      if (isCreate) {
        setTags(
          dedupeTags([...tags, saved]).sort((a, b) =>
            collator.compare(a.TagID, b.TagID),
          ),
        );
      } else {
        const idx = tags.findIndex(
          (t) =>
            String(t.TagID || "").toLowerCase() ===
            String(patchTargetTagId || "").toLowerCase(),
        );

        if (idx >= 0) {
          const updated = [...tags];
          updated[idx] = saved;
          setTags(
            dedupeTags(updated).sort((a, b) =>
              collator.compare(a.TagID, b.TagID),
            ),
          );
        } else {
          // If list was stale, append
          setTags(
            dedupeTags([...tags, saved]).sort((a, b) =>
              collator.compare(a.TagID, b.TagID),
            ),
          );
        }
      }
    } catch (e) {
      console.error("Save tag failed:", e);
      alert("Save failed.");
    } finally {
      handleCancelTag();
    }
  };

  /* search/sort */
  const handleSort = (key) =>
    sortBy === key
      ? setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
      : (setSortBy(key), setSortDirection("asc"));

  const filteredTags = useMemo(() => {
    const term = String(searchTerm || "")
      .toLowerCase()
      .trim();
    const base = Array.isArray(tags) ? tags : [];

    const result = !term
      ? base
      : base.filter((t) => {
          const id = String(t.TagID || "").toLowerCase();
          const desc = String(t.TagDesc || "").toLowerCase();
          return id.includes(term) || desc.includes(term);
        });

    return [...result].sort((a, b) => {
      const av = a?.[sortBy] ?? "";
      const bv = b?.[sortBy] ?? "";
      const res = collator.compare(String(av), String(bv));
      return sortDirection === "asc" ? res : -res;
    });
  }, [tags, searchTerm, sortBy, sortDirection]);

  /* CSV export */
  const handleExportTags = () => {
    if (!filteredTags.length) return;
    const hdr = ["TagID", "TagDesc"];
    const rows = filteredTags.map((t) => [t.TagID, t.TagDesc]);
    const csv = [hdr, ...rows]
      .map((r) =>
        r.map((f) => `"${String(f ?? "").replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tags_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <div className="p-2">
        <div
          className={`rounded p-3 text-sm ${
            theme === "dark"
              ? "bg-gray-800 text-gray-200 border border-gray-700"
              : "bg-gray-50 text-gray-700 border border-gray-200"
          }`}
        >
          Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-2">
        {/* Search */}
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className={`absolute left-3 top-2 ${
              theme === "dark" ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <input
            type="text"
            placeholder="Search tags…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-9 pr-3 py-1 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === "dark"
                ? "bg-gray-700 text-gray-100 border border-gray-600"
                : "bg-white text-gray-700 border border-gray-300"
            }`}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap items-center">
          <button
            onClick={handleCreateTag}
            className="flex items-center font-bold py-1 px-3 rounded-md shadow-sm bg-blue-500 text-white hover:bg-blue-600"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Tag
          </button>

          <button
            onClick={handleExportTags}
            className="flex items-center font-bold py-1 px-3 rounded-md shadow-sm bg-green-500 text-white hover:bg-green-600"
          >
            <FontAwesomeIcon icon={faFileExport} className="mr-2" />
            Export
          </button>

          <button
            onClick={fetchTags}
            className={`flex items-center font-bold py-1 px-3 rounded-md shadow-sm ${
              theme === "dark"
                ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                : "bg-gray-200 hover:bg-gray-300 text-gray-800"
            }`}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faTag} className="mr-2" />
            Refresh
          </button>

          {loading && (
            <span
              className={`text-xs ${
                theme === "dark" ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Loading…
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      {filteredTags && filteredTags.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead
              className={`border-b ${
                theme === "dark"
                  ? "bg-gray-700 text-gray-100 border-gray-600"
                  : "bg-gray-100 text-gray-800 border-gray-200"
              }`}
            >
              <tr>
                <SortableHeader
                  label="TagID"
                  columnKey="TagID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Tag Description"
                  columnKey="TagDesc"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <th className="py-1 px-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTags.map((t) => (
                <tr
                  key={t.TagID}
                  className={`border-b ${
                    theme === "dark"
                      ? "border-gray-600 hover:bg-gray-600"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <td className="py-1 px-2 font-mono">{t.TagID}</td>
                  <td className="py-1 px-2">{t.TagDesc}</td>
                  <td className="py-1 px-2">
                    <button
                      onClick={() => handleEditTag(t)}
                      className="inline-flex items-center px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white text-xs"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-300">
          {loading ? "Loading tags…" : "No tags found."}
        </p>
      )}

      {/* Create/Edit Modal */}
      {showTagModal && editingTag && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 ${
            theme === "dark"
              ? "bg-black bg-opacity-80"
              : "bg-black bg-opacity-50"
          }`}
        >
          <div
            className={`w-full max-w-lg p-4 rounded-lg shadow-2xl relative ${
              theme === "dark"
                ? "bg-gray-800 text-gray-100"
                : "bg-white text-gray-900"
            } max-h-[85vh] overflow-y-auto`}
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-2 sticky top-0 bg-inherit">
              <h4 className="text-base font-bold">
                {tagModalMode === "edit" ? "Edit Tag" : "Create Tag"}
              </h4>
              <button
                onClick={handleCancelTag}
                className="p-1 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Fields */}
            <div className="grid grid-cols-1 gap-2">
              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  TagID
                </label>
                <input
                  name="TagID"
                  value={editingTag.TagID}
                  onChange={handleModalFieldChange}
                  disabled={tagModalMode === "edit"}
                  className={`${getInputClasses(theme)} ${
                    tagModalMode === "edit"
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }`}
                />
                {tagModalMode === "edit" && (
                  <div className="text-[11px] text-gray-500 mt-1">
                    TagID is locked while editing.
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Tag Description
                </label>
                <input
                  name="TagDesc"
                  value={editingTag.TagDesc}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={handleCancelTag}
                className={`px-3 py-1 rounded text-sm ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTag}
                className="px-3 py-1 rounded text-sm bg-blue-500 hover:bg-blue-600 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────── SortableHeader ───────── */
function SortableHeader({
  label,
  columnKey,
  sortBy,
  sortDirection,
  onSort,
  theme,
}) {
  const active = sortBy === columnKey;
  return (
    <th
      onClick={() => onSort(columnKey)}
      className="py-1 px-2 text-left cursor-pointer select-none"
    >
      <span className="flex items-center space-x-1">
        <span>{label}</span>
        {active && (
          <FontAwesomeIcon
            icon={sortDirection === "asc" ? faSortUp : faSortDown}
            className={theme === "dark" ? "text-gray-200" : "text-gray-600"}
          />
        )}
      </span>
    </th>
  );
}

/* allow either import style if you ever want it */
export { TagsTab };
export default TagsTab;
